import type { FormEvent } from 'react';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CONFIG } from 'src/config-global';

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type FormStatus = {
  tone: 'ok' | 'err';
  message: string;
};

function readField(data: FormData, key: string) {
  const value = data.get(key);
  return typeof value === 'string' ? value.trim() : '';
}

export function useContactForm() {
  const { t } = useTranslation('contact');
  const [status, setStatus] = useState<FormStatus | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const form = event.currentTarget;
      const data = new FormData(form);

      const payload = {
        name: readField(data, 'name'),
        email: readField(data, 'email'),
        company: readField(data, 'company'),
        message: readField(data, 'message'),
        consent: data.get('consent') !== null,
      };

      if (!payload.name || !payload.email || !payload.message || !payload.consent) {
        setStatus({ tone: 'err', message: t('form.status.missingFields') });
        return;
      }

      if (!EMAIL_PATTERN.test(payload.email)) {
        setStatus({ tone: 'err', message: t('form.status.invalidEmail') });
        return;
      }

      if (!CONFIG.contactFormEndpoint) {
        setStatus({ tone: 'ok', message: t('form.status.accepted') });
        form.reset();
        return;
      }

      setSubmitting(true);

      try {
        const response = await fetch(CONFIG.contactFormEndpoint, {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error('Contact form request failed');
        }

        setStatus({ tone: 'ok', message: t('form.status.sent') });
        form.reset();
      } catch {
        setStatus({ tone: 'err', message: t('form.status.failed') });
      } finally {
        setSubmitting(false);
      }
    },
    [t]
  );

  return { status, submitting, handleSubmit };
}
